import React, { useState, useEffect } from "react";
import { Modal, Tooltip } from "antd";
import { ICONS } from "../../../assets/icons/index";
import { cn } from "../../../helper/cn";
import Select from "../../../components/commonComponents/select";
import Header from "../../../components/commonComponents/modals/Header";
import FullArrow from "../../../assets/customSVG/FullArrow";
import Loader from "../../../components/commonComponents/Loader/Index";
import { creditsSendApi } from "../api/Api";
import { billingPlanPricingApi } from "../../../api/Api";
import PricingPlanModal from "./PricingPlanModal";

export const CheckoutContent = ({
  isPlanSelected,
  locationError,
  brandsData,
  creditsResponse,
  dynamicInputs,
  editInfo,
  setEditInfo,
  handleUpdate,
  handleBrandChange,
  input,
  inputContainer,
  buttonClass,
  category,
  stateList,
  cityList,
  buttonEnable,
  handlePayment,
  currentAccount,
  selectedPlan,
  selectedPrice,
  createLoader,
  handleViewPlans,
  onOpenPricingModal
}) => {
  // Use selected plan info if available (from PricingPlanModal), otherwise use current brand data
  const displayPlanCode = selectedPlan?.code || brandsData?.billing_plan_code;
  const displayPlanName = selectedPlan?.title || brandsData?.billing_plan_code;

  // Determine default billing cycle based on source
  const getDefaultBillingCycle = () => {
    // From PricingPlanModal (upgrade flow)
    if (selectedPrice) return selectedPrice.toLowerCase();

    // From creditsResponse
    if (creditsResponse?.billing_cycle) return creditsResponse.billing_cycle.toLowerCase();

    // From BrandOverview (renew flow)
    if (brandsData?.billing_cycle) return brandsData.billing_cycle.toLowerCase();

    return "monthly"; // fallback
  };

  // Check if user is upgrading from a yearly plan
  // If current brand is on yearly and upgrading to a different plan, lock to yearly only
  const isUpgradingFromYearly = () => {
    const currentBillingCycle = brandsData?.billing_cycle?.toLowerCase();
    const isUpgrading = selectedPlan && selectedPlan.code !== brandsData?.billing_plan_code?.toLowerCase();
    return currentBillingCycle === "yearly" && isUpgrading;
  };

  const isYearlyLocked = isUpgradingFromYearly();

  // State management for billing cycle switching
  const [selectedBillingCycle, setSelectedBillingCycle] = useState(
    isYearlyLocked ? "yearly" : getDefaultBillingCycle()
  );
  const [isLoadingBillSummary, setIsLoadingBillSummary] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const [currentBillSummary, setCurrentBillSummary] = useState(creditsResponse);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);

  // Force yearly selection if locked and refetch bill summary
  useEffect(() => {
    if (isYearlyLocked && selectedBillingCycle !== "yearly") {
      setSelectedBillingCycle("yearly");
    }

    // Check all possible locations for billing_cycle
    const currentCycle = creditsResponse?.billing_cycle
      || creditsResponse?.credit_item?.billing_cycle
      || creditsResponse?.settings?.subscription?.billing_cycle;

    // If locked to yearly but creditsResponse has monthly billing, refetch with yearly
    if (isYearlyLocked && currentCycle?.toLowerCase() === "monthly") {
      const refetchWithYearly = async () => {
        setIsLoadingBillSummary(true);
        try {
          const payload = {
            brand_id: brandsData?.id,
            transaction_type: "SUBSCRIPTION",
            billing_plan_name: displayPlanCode,
            billing_cycle: "yearly",
          };

          const response = await creditsSendApi(payload);

          if (response?.status === 200 || response?.status === 201) {
            setCurrentBillSummary(response?.data);
          }
        } catch (error) {
          console.error("Error fetching yearly billing data:", error);
        } finally {
          setIsLoadingBillSummary(false);
        }
      };

      refetchWithYearly();
    }
  }, [isYearlyLocked, selectedBillingCycle, creditsResponse, brandsData?.id, displayPlanCode]);

  // Fetch pricing data on mount
  useEffect(() => {
    const fetchPricing = async () => {
      if (creditsResponse?.transaction_type !== "SUBSCRIPTION") {
        setIsLoadingPricing(false);
        return;
      }

      setIsLoadingPricing(true);
      try {
        const response = await billingPlanPricingApi(brandsData?.id);

        if (response?.status === 200 && response?.data) {
          // Find the current plan from the array
          const currentPlan = response.data.find(plan => plan.code === displayPlanCode);
          setPricingData(currentPlan);
        }
      } catch (error) {
        console.error("Error fetching pricing data:", error);
      } finally {
        setIsLoadingPricing(false);
      }
    };

    fetchPricing();
  }, [brandsData?.id, displayPlanCode]);

  // Handler for billing cycle change
  const handleBillingCycleChange = async (newCycle) => {
    if (newCycle === selectedBillingCycle || isLoadingBillSummary) return;

    // Prevent switching to monthly if locked to yearly
    if (isYearlyLocked && newCycle === "monthly") return;

    const previousCycle = selectedBillingCycle;
    setSelectedBillingCycle(newCycle);
    setIsLoadingBillSummary(true);

    try {
      const payload = {
        brand_id: brandsData?.id,
        transaction_type: "SUBSCRIPTION",
        billing_plan_name: displayPlanCode,
        billing_cycle: newCycle,
      };

      const response = await creditsSendApi(payload);

      if (response?.status === 200 || response?.status === 201) {
        setCurrentBillSummary(response?.data);
      }
    } catch (error) {
      console.error("Error fetching billing cycle data:", error);
      // Revert selection on error
      setSelectedBillingCycle(previousCycle);
    } finally {
      setIsLoadingBillSummary(false);
    }
  };

  // Wrapper for handlePayment to ensure correct billing cycle is used
  const handleProceedToCheckout = () => {
    // Pass the current bill summary data to handlePayment
    // This ensures the payment is created with the correct billing cycle
    if (handlePayment) {
      handlePayment(currentBillSummary, selectedBillingCycle);
    }
  };

  // Handle view plans click - use handleViewPlans if provided (from PricingPlanModal),
  // otherwise use onOpenPricingModal (from CheckoutModal)
  const handleViewPlansClick = () => {
    if (handleViewPlans) {
      handleViewPlans();
    } else if (onOpenPricingModal) {
      onOpenPricingModal();
    }
  };

  return (
    <div
      className={`w-full flex gap-5 text-[var(--contentText)] px-[2vw]`}
    >
      <div className="w-3/4 h-auto flex-column rounded-2xl">
        {/* Billing Cycle Selection - Only for SUBSCRIPTION type */}
        {creditsResponse?.transaction_type === "SUBSCRIPTION" ? (
          <div className="w-100 flex flex-col gap-3 mb-4">
            {/* Monthly Radio Card */}
            <div
              className={cn(
                "w-100 bg-white rounded-xl p-4 border-2 transition-all",
                selectedBillingCycle === "monthly"
                  ? "border-[#6940F2] bg-[#F9F6FF]"
                  : "border-[var(--border-100)] hover:border-[#6940F2]/40",
                isLoadingBillSummary && "opacity-60 cursor-not-allowed",
                isYearlyLocked ? "opacity-50 cursor-not-allowed bg-gray-50" : "cursor-pointer"
              )}
              onClick={() => !isLoadingBillSummary && !isYearlyLocked && handleBillingCycleChange("monthly")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Radio Button */}
                  <div
                    className={cn(
                      "size-5 rounded-full border-2 flex items-center justify-center transition-all",
                      selectedBillingCycle === "monthly"
                        ? "border-[#6940F2]"
                        : "border-[var(--border-input)]",
                      isYearlyLocked && "border-gray-300"
                    )}
                  >
                    {selectedBillingCycle === "monthly" && (
                      <div className="size-3 rounded-full bg-[#6940F2]" />
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <h5 className={cn(
                        "font16px-600 capitalize",
                        isYearlyLocked ? "text-gray-400" : "text-[var(--textBlack)]"
                      )}>
                        {displayPlanName} - Monthly
                      </h5>
                      {isYearlyLocked && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Not available
                        </span>
                      )}
                    </div>
                    <span className={cn(
                      "font14px-400",
                      isYearlyLocked ? "text-gray-400" : "text-[var(--font-400)]"
                    )}>
                      {isYearlyLocked ? "You are currently on a yearly plan" : "Billed monthly"}
                    </span>
                  </div>
                </div>

                <div className={cn(
                  "font20px-600",
                  isYearlyLocked ? "text-gray-400" : "text-[var(--textBlack)]"
                )}>
                  {isLoadingPricing ? (
                    <Loader Width={20} Height={20} />
                  ) : (
                    <>
                      {brandsData?.brand_currency_symbol}
                      {pricingData?.monthly_price || "--"}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Yearly Radio Card */}
            <div
              className={cn(
                "w-100 bg-white rounded-xl p-4 border-2 transition-all cursor-pointer",
                selectedBillingCycle === "yearly"
                  ? "border-[#6940F2] bg-[#F9F6FF]"
                  : "border-[var(--border-100)] hover:border-[#6940F2]/40",
                isLoadingBillSummary && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => !isLoadingBillSummary && handleBillingCycleChange("yearly")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Radio Button */}
                  <div
                    className={cn(
                      "size-5 rounded-full border-2 flex items-center justify-center transition-all",
                      selectedBillingCycle === "yearly"
                        ? "border-[#6940F2]"
                        : "border-[var(--border-input)]"
                    )}
                  >
                    {selectedBillingCycle === "yearly" && (
                      <div className="size-3 rounded-full bg-[#6940F2]" />
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <h5 className="font16px-600 text-[var(--textBlack)] capitalize">
                      {displayPlanName} - Yearly
                    </h5>
                    <span className="font14px-400 text-[var(--font-400)]">
                      Billed annually
                    </span>
                  </div>
                </div>

                <div className="font20px-600 text-[var(--textBlack)]">
                  {isLoadingPricing ? (
                    <Loader Width={20} Height={20} />
                  ) : (
                    <>
                      {brandsData?.brand_currency_symbol}
                      {pricingData?.annual_price || "--"}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Credits Display - Non-subscription */
          <div className="w-100 bg-white rounded-xl p-2 mb-4 border border-[var(--border-100)]">
            <div className="flex items-center justify-between">
              <h5 className="font16px-600 text-[var(--textBlack)]">
                WhatsApp Conversation Credit Charges
              </h5>
              <div className="font20px-600 text-[var(--textBlack)]">
                {brandsData?.brand_currency_symbol}
                {creditsResponse?.amount}
              </div>
            </div>
          </div>
        )}

        {/* Brand Information */}
        <div className="w-100 bg-white b-radius mt-10">
          <span className="font16px-600 text-[var(--textBlack)]">
            Brand Information
          </span>

          <div className="grid grid-cols-2 gap-3 mt-15 max-h-[55vh] overflow-y-auto">
            {dynamicInputs
              ?.filter(
                (item) =>
                  item?.id !== 10 || brandsData?.brand_currency_code === "INR"
              )
              .map((item) => {
                return (
                  <div className="mb-10" key={item?.id}>
                    <span className="font14px-500 text-[var(--font-600)]">
                      {item?.label}
                      {item?.id !== 10 && item?.id !== 8 && (
                        <span className="text-[red]">*</span>
                      )}
                    </span>
                    {editInfo ? (
                      <div
                        className={cn(
                          "w-full flex items-center mt-5 pl-10 gap-2.5 border border-[var(--border-input)] rounded-lg",
                          item?.id === 6 && "bg-gray-100 cursor-not-allowed"
                        )}
                      >
                        {item?.id !== 6 &&
                          item?.id !== 7 &&
                          item?.id !== 8 &&
                          item?.id !== 2 && (
                            <input
                              type={item?.id === 9 ? "number" : "text"}
                              className={cn(input)}
                              name="name"
                              value={item?.value || ""}
                              placeholder="E.g. Spring sale offer"
                              onChange={(e) =>
                                handleBrandChange({
                                  id: item?.id,
                                  value: e.target.value,
                                })
                              }
                              autoComplete="off"
                              maxLength={item?.id === 9 ? 6 : 64}
                            />
                          )}
                        {item?.id === 6 ? (
                          <div className="w-full">
                            <input
                              type="text"
                              className={cn(input)}
                              value={item?.value || ""}
                              readOnly
                              disabled
                            />
                          </div>
                        ) : [7, 8, 2].includes(item?.id) ? (
                          <Select
                            value={item?.value || ""}
                            options={
                              item?.id === 7
                                ? stateList || []
                                : item?.id === 8
                                ? cityList || []
                                : category || []
                            }
                            onChange={(value) => {
                              handleBrandChange({
                                id: item?.id,
                                value: value?.label,
                                locations_id: value?.id,
                              });
                            }}
                            rootClassName="border-none w-100"
                            placeholder={"--Select option--"}
                            position="bottom"
                            optionClassName="p-20 border-b last:border-none"
                            labelClassName="!font14px-500 !text-[var(--textBlack)]"
                          />
                        ) : null}
                      </div>
                    ) : (
                      <>
                        {item?.value?.length > 40 ? (
                          <Tooltip title={item?.value} placement="top">
                            <div
                              className={cn(
                                inputContainer,
                                "bg-[var(--BG-25)] pl-10 font14px-500 text-[var(--textBlack)]"
                              )}
                            >
                              <p className="max-w-[30ch] truncate">
                                {item?.value}
                              </p>
                            </div>
                          </Tooltip>
                        ) : (
                          <div
                            className={cn(
                              inputContainer,
                              "bg-[var(--BG-25)] pl-10 font14px-500 text-[var(--textBlack)]"
                            )}
                          >
                            {item?.value || "--"}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
          </div>
          {locationError && (
            <div className="flex-row align-center">
              <img src={ICONS?.alertIconError} alt="" />
              <span className="capitalize error-message pl-5">
                {locationError}
              </span>
            </div>
          )}
          {(currentAccount?.role === "Account Owner" ||
            currentAccount?.role === "Account Manager") && (
            <div className="w-100 justify-end flex-row mb-20">
              <button
                className={buttonClass(editInfo) + " p-2"}
                onClick={() => {
                  if (editInfo) {
                    handleUpdate();
                  } else {
                    setEditInfo(true);
                  }
                }}
              >
                {!editInfo ? "Edit Brand Info" : "Update Brand Info"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bill Summary */}
      <div className="w-2/4 flex flex-col bg-[#FBFBFB] px-7 pt-2.5 mb-15 rounded-2xl relative">
        {/* Loading Overlay */}
        {isLoadingBillSummary && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl z-10 flex items-center justify-center">
            <Loader Width={40} Height={40} />
          </div>
        )}

        <div className="flex-1">
          <h6 className="font16px-600">Bill Summary:</h6>

          {/* Coupon Code Section - Commented for future use */}
          {/* <div className="mt-5 mb-4">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Enter coupon code"
                className="flex-1 h-10 px-3 border border-[var(--border-input)] rounded-lg font14px-400 outline-none focus:border-[var(--new-primary)]"
                // value={couponCode}
                // onChange={(e) => setCouponCode(e.target.value)}
              />
              <button className="h-10 px-4 bg-[var(--new-primary)] text-white rounded-lg font14px-600 hover:opacity-90">
                Apply
              </button>
            </div>
          </div> */}

          <div className="flex flex-col gap-3 pt-7">
            {(currentBillSummary?.credit_item?.settings?.subscription?.proration || currentBillSummary?.settings?.subscription?.proration) ? (
              <>
                {/* Plan Amount */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500 capitalize">
                    {(currentBillSummary?.credit_item?.transaction_type || currentBillSummary?.transaction_type) === "SUBSCRIPTION"
                      ? `${displayPlanName} Plan (${selectedBillingCycle})`
                      : "Credit Amount"}
                  </span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}
                    {currentBillSummary?.credit_item?.settings?.subscription?.proration?.base_amount || currentBillSummary?.settings?.subscription?.proration?.base_amount}
                  </p>
                </div>

                {/* Prorated Credit */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500">Prorated Credit</span>
                  <p className="font14px-600 text-[#0F9960]">
                    - {brandsData?.brand_currency_symbol}
                    {currentBillSummary?.credit_item?.settings?.subscription?.proration?.prorated_credit || currentBillSummary?.settings?.subscription?.proration?.prorated_credit}
                  </p>
                </div>

                {/* Plan Discount */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500">Plan Discount</span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}0
                  </p>
                </div>

                {/* Subtotal */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)] pt-2 pb-3 border-t border-dashed border-[var(--border-100)]">
                  <span className="font14px-600">Subtotal</span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}
                    {currentBillSummary?.credit_item?.settings?.subscription?.proration?.actual_charged || currentBillSummary?.settings?.subscription?.proration?.actual_charged}
                  </p>
                </div>

                {/* Coupon Discount - Commented for future use */}
                {/* <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500">Coupon Discount</span>
                  <p className="font14px-600 text-[#0F9960]">
                    - {brandsData?.brand_currency_symbol}0
                  </p>
                </div> */}

                {/* Taxable Amount */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500">Taxable Amount</span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}
                    {currentBillSummary?.credit_item?.settings?.subscription?.proration?.actual_charged || currentBillSummary?.settings?.subscription?.proration?.actual_charged}
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Plan/Credit Amount */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500 capitalize">
                    {(currentBillSummary?.credit_item?.transaction_type || currentBillSummary?.transaction_type) === "SUBSCRIPTION"
                      ? `${displayPlanName} Plan (${selectedBillingCycle})`
                      : "Credit Amount"}
                  </span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}
                    {currentBillSummary?.credit_item?.amount || currentBillSummary?.amount}
                  </p>
                </div>

                {/* Plan Discount */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500">Discount</span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}0
                  </p>
                </div>

                {/* Subtotal */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)] pt-2 pb-3 border-t border-dashed border-[var(--border-100)]">
                  <span className="font14px-600">Subtotal</span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}
                    {currentBillSummary?.credit_item?.amount || currentBillSummary?.amount}
                  </p>
                </div>

                {/* Coupon Discount - Commented for future use */}
                {/* <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500">Coupon Discount</span>
                  <p className="font14px-600 text-[#0F9960]">
                    - {brandsData?.brand_currency_symbol}0
                  </p>
                </div> */}

                {/* Taxable Amount */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500">Taxable Amount</span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}
                    {currentBillSummary?.credit_item?.amount || currentBillSummary?.amount}
                  </p>
                </div>
              </>
            )}

            {/* Tax Section */}
            {brandsData?.brand_currency_code === "INR" ? (
              <>
                {/* CGST (9%) */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)] pt-3 border-t border-dashed border-[var(--border-100)]">
                  <span className="font14px-500">CGST (9%)</span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}
                    {(parseFloat(currentBillSummary?.credit_item?.tax_amount || currentBillSummary?.tax_amount || 0) / 2).toFixed(2)}
                  </p>
                </div>

                {/* SGST (9%) */}
                <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)]">
                  <span className="font14px-500">SGST (9%)</span>
                  <p className="font14px-600">
                    {brandsData?.brand_currency_symbol}
                    {(parseFloat(currentBillSummary?.credit_item?.tax_amount || currentBillSummary?.tax_amount || 0) / 2).toFixed(2)}
                  </p>
                </div>
              </>
            ) : null}

            {/* Total Amount */}
            <div className="flex items-center justify-between gap-2.5 text-[var(--textBlack)] pt-5 mt-2 border-t-2 border-[var(--border-100)]">
              <span className="font16px-600">Total Amount</span>
              <p className="font20px-600">
                {brandsData?.brand_currency_symbol}
                {currentBillSummary?.credit_item?.total_cost || currentBillSummary?.total_cost}
              </p>
            </div>
          </div>
        </div>
        <div className="w-full flex flex-col gap-2">
          <button
            className={`w-full h-12 rounded-md ${
              !buttonEnable()
                ? "bg-[var(--disable-bg)] text-[var(--disable-color)] cursor-not-allowed"
                : "bg-[var(--new-primary)] text-white"
            } text-base weight-bold flex items-center justify-center gap-2`}
            onClick={() => buttonEnable() && !createLoader && handleProceedToCheckout()}
            disabled={!buttonEnable() || createLoader}
          >
            {createLoader ? <Loader Width={20} Height={20} loaderColor="white" loaderBg="rgba(255, 255, 255, 0.3)" /> : "Proceed to Checkout"}
          </button>

          {/* Link to view other plans */}
          {(handleViewPlans || onOpenPricingModal) && creditsResponse?.transaction_type === "SUBSCRIPTION" && (
            <button
              onClick={handleViewPlansClick}
              className="w-full text-sm font-medium text-[#6940F2] hover:text-[#5835D1] transition-colors duration-200 underline py-2"
            >
              Want to explore other plans?
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CheckoutModal = (props) => {
  const { open, setOpen, isPlanSelected, handleBackPlan, brandsData } = props;
  const [showPricingModal, setShowPricingModal] = useState(false);

  const handleOpenPricingModal = () => {
    setShowPricingModal(true);
  };

  const handleClosePricingModal = (shouldCloseCheckout = false) => {
    setShowPricingModal(false);
    // If payment was successful, close checkout modal too
    if (shouldCloseCheckout) {
      setOpen(false);
    }
    // Otherwise, CheckoutModal will show again automatically
  };

  return (
    <>
      <Modal
        styles={{ content: { padding: 0 } }}
        open={open && !showPricingModal}
        afterOpenChange={(modalOpen) => {
          // Only update parent state if we're not switching to PricingPlanModal
          if (!modalOpen && !showPricingModal) {
            // User closed the modal directly, not switching to pricing
            setOpen(false);
          }
        }}
        centered
        footer={null}
        closable={false}
        mask={true}
        wrapClassName={"bg-[#00000095]"}
        destroyOnClose
        width={"80vw"}
        zIndex={1110}
        style={{
          borderRadius: 20,
          boxShadow: "0px 10px 24px 0px rgba(79, 92, 128, 0.15)",
          overflow: "hidden",
        }}
      >
        <Header
          title={"Proceed to Checkout"}
          prefix={
            isPlanSelected ? (
              <div
                className="rotate-180 size-9 flex items-center justify-center border border-[var(--border-100)] rounded-lg cursor-pointer"
                onClick={() => {
                  handleBackPlan();
                }}
              >
                <FullArrow width={14} height={14} />
              </div>
            ) : null
          }
          prefixStyle="mr-4"
          titleSize="largest"
          handleCancel={() => setOpen(false)}
        />
        <CheckoutContent {...props} onOpenPricingModal={handleOpenPricingModal} />
      </Modal>

      {/* Pricing Plan Modal */}
      <PricingPlanModal
        open={showPricingModal}
        setOpen={(shouldOpen) => {
          if (shouldOpen) {
            setShowPricingModal(true);
          } else {
            // When closing PricingPlanModal, return to CheckoutModal
            handleClosePricingModal(false);
          }
        }}
        brandId={brandsData?.id}
        brandsData={brandsData}
        onSuccess={() => {
          // Close both modals after successful payment
          handleClosePricingModal(true);
        }}
      />
    </>
  );
};

export default CheckoutModal;
